import { weekIdToTimestamp } from 'src/utils/dateUtils';
import { calculatePercentChange, safeDivide } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { ComparisonPeriod } from 'src/utils/Hooks';
import {
  TrafficSummaryResponse,
  MarketShareResponse,
  MarketShareValues,
  AdditionalMetricData,
  FormattedMarketShare,
  WeeklyMarketShareRecord
} from './types';

/**
 * Type for the metric formatter function
 */
type MetricFormatter = (
  value: number,
  metricType: string,
  options?: { showFullValue?: boolean; decimalPlaces?: number; showNegative?: boolean }
) => string;

/**
 * Standard formatting options for metrics
 */
const getFormatOptions = (metricTypeToUse: string) => ({
  showFullValue: metricTypeToUse === METRICTYPE.PERCENT,
  decimalPlaces: 2,
  showNegative: true
});

/**
 * Options for getSplineDataFromResponse
 */
export interface GetSplineDataFromResponseOptions {
  /**
   * When true, missing weeks are filled with 0; when false, with null.
   * @default true — we pad with 0 (product decision) so the chart line connects across the full range.
   */
  fillNullWithZero?: boolean;
}

/**
 * Converts raw response data to time series data for charts.
 * Output format: [[timestamp, value], ...]
 *
 * Missing weeks are padded with 0 by default (product decision) so the chart
 * line connects across the full range. Pass fillNullWithZero: false to use null.
 *
 * @param response - The API response containing records or aggregate value
 * @param weekIds - Expected week IDs for the time period
 * @param metricField - The metric field name to extract
 * @param isMarketShare - Whether this is market share data (unused, kept for backwards compatibility)
 * @param options - Optional configuration for data handling
 * @returns Array of [timestamp, value] tuples; missing weeks are 0 by default
 */
export const getSplineDataFromResponse = (
  response: TrafficSummaryResponse | MarketShareResponse,
  weekIds: number[],
  metricField: string,
  isMarketShare: boolean = false,
  options: GetSplineDataFromResponseOptions = {}
): number[][] => {
  // Pad missing data with 0 by default (product decision: smooth line across full range).
  const { fillNullWithZero = true } = options;
  const missingValue = fillNullWithZero ? 0 : null;

  if (!response) {
    return [];
  }

  // Check if the response has records array (new API format)
  if (response.records && Array.isArray(response.records)) {
    // Create a lookup map for O(1) access by weekId
    const recordsByWeekId = new Map<number, number | null>();
    response.records.forEach((record) => {
      recordsByWeekId.set(record.weekId, record.value ?? missingValue);
    });

    // Map expected weekIds to values, filling nulls (or 0) for missing weeks
    // This ensures proper alignment when comparison data has gaps
    return weekIds.map((weekId) => {
      const value = recordsByWeekId.has(weekId) ? recordsByWeekId.get(weekId) : missingValue;
      // If fillNullWithZero is true, also convert any null values to 0
      const finalValue = fillNullWithZero && value === null ? 0 : value;
      return [weekIdToTimestamp(weekId), finalValue as number];
    });
  }

  // Fallback to old format (direct property access)
  const metricValue = response[metricField] || response[response.metricType] || 0;

  // Distribute the total value evenly across the weeks
  const valuePerWeek = metricValue / weekIds.length;

  return weekIds.map((weekId) => [weekIdToTimestamp(weekId), valuePerWeek]);
};

/**
 * Converts weekly market share records to spline chart format
 */
export const weeklyMarketShareToSplineData = (weeklyMarketShare: WeeklyMarketShareRecord[] | undefined): number[][] => {
  if (!weeklyMarketShare) return [];
  return weeklyMarketShare.map((record) => [weekIdToTimestamp(record.weekId), record.value ?? 0]);
};

/**
 * Builds primary spline data.
 * Note: Prior period connection point is NOT added here - SplineChartV2's useSplineChartV2Data
 * handles the merging of comparison and primary data for prior period mode.
 *
 * @param primData - Primary spline data in format [[timestamp, value], ...]
 * @returns The primary spline data unchanged
 */
export const buildPrimarySplineData = (primData: number[][]): number[][] => {
  // Return raw primary data without connection point
  // SplineChartV2 handles prior period merging via useSplineChartV2Data
  return primData;
};

/**
 * Builds market share spline data.
 * Note: Prior period connection point is NOT added here - SplineChartV2's useSplineChartV2Data
 * handles the merging of comparison and primary data for prior period mode.
 *
 * @param primaryWeeklyShare - Primary weekly market share records
 * @returns Market share data in spline format [[timestamp, value], ...]
 */
export const buildMarketShareSplineData = (primaryWeeklyShare: WeeklyMarketShareRecord[] | undefined): number[][] => {
  if (!primaryWeeklyShare) return [];

  // Return raw primary market share data without connection point
  // SplineChartV2 handles prior period merging via useSplineChartV2Data
  return weeklyMarketShareToSplineData(primaryWeeklyShare);
};

/**
 * Formats additional metrics fields
 */
export const formatAdditionalMetrics = (
  additionalMetricFields: string[],
  primaryData: TrafficSummaryResponse | undefined,
  comparisonData: TrafficSummaryResponse | undefined,
  skipComparison: boolean,
  formatMetric: MetricFormatter,
  metricTypeToUse: string
): Record<string, AdditionalMetricData> => {
  return additionalMetricFields.reduce((acc, field) => {
    const primaryValue = primaryData ? primaryData[field] || 0 : 0;
    const comparisonValue = !skipComparison && comparisonData ? comparisonData[field] || 0 : 0;
    const fieldPercentChange = skipComparison ? 0 : calculatePercentChange(primaryValue, comparisonValue);

    acc[field] = {
      primaryValue,
      comparisonValue,
      percentChange: fieldPercentChange,
      formattedPrimaryValue: formatMetric(primaryValue, metricTypeToUse, getFormatOptions(metricTypeToUse)),
      formattedComparisonValue: formatMetric(comparisonValue, metricTypeToUse, getFormatOptions(metricTypeToUse)),
      formattedPercentChange: formatMetric(Math.abs(fieldPercentChange), METRICTYPE.PERCENT, {
        decimalPlaces: 2
      })
    };

    return acc;
  }, {} as Record<string, AdditionalMetricData>);
};

/**
 * Formats market share data for a single period
 */
const formatMarketSharePeriod = (
  marketShareData: {
    marketShareValue: number;
    clientValue: number;
    marketValue: number;
    weeklyMarketShare?: WeeklyMarketShareRecord[];
  },
  formatMetric: MetricFormatter,
  metricTypeToUse: string
) => ({
  ...marketShareData,
  formattedMarketShareValue: formatMetric(marketShareData.marketShareValue, metricTypeToUse, {
    decimalPlaces: 2,
    showFullValue: true
  }),
  formattedClientValue: formatMetric(marketShareData.clientValue, metricTypeToUse, {
    decimalPlaces: 0
  }),
  formattedMarketValue: formatMetric(marketShareData.marketValue, metricTypeToUse, {
    decimalPlaces: 0
  }),
  records: marketShareData.weeklyMarketShare || []
});

/**
 * Formats market share values for both periods
 */
export const formatMarketShareValues = (
  marketShareValues: MarketShareValues,
  formatMetric: MetricFormatter,
  metricTypeToUse: string
): FormattedMarketShare => ({
  primary: formatMarketSharePeriod(marketShareValues.primary, formatMetric, metricTypeToUse),
  comparison: formatMarketSharePeriod(marketShareValues.comparison, formatMetric, metricTypeToUse)
});

/**
 * Formats all metric values for the hook's return object
 */
export interface FormatMetricValuesInput {
  primaryTotal: number;
  secondaryTotal: number;
  lastPrimaryValue: number;
  lastSecondaryValue: number;
  percentChange: number;
  percentChangeLastValues: number;
  primaryDataLength: number;
  overlappedComparisonCount: number;
}

export interface FormattedMetricValues {
  formattedPrimaryTotal: string;
  formattedSecondaryTotal: string;
  formattedLastPrimaryValue: string;
  formattedLastSecondaryValue: string;
  formattedPercentChange: string;
  formattedPercentChangeLastValues: string;
  primaryAverageRate: number;
  secondaryAverageRate: number;
  formattedPrimaryAverageRate: string;
  formattedSecondaryAverageRate: string;
}

export const formatMetricValues = (
  input: FormatMetricValuesInput,
  formatMetric: MetricFormatter,
  metricTypeToUse: string
): FormattedMetricValues => {
  const formatOptions = getFormatOptions(metricTypeToUse);

  const primaryAverageRate = safeDivide(input.primaryTotal, input.primaryDataLength);
  const secondaryAverageRate = safeDivide(input.secondaryTotal, input.overlappedComparisonCount);

  return {
    formattedPrimaryTotal: formatMetric(input.primaryTotal, metricTypeToUse, formatOptions),
    formattedSecondaryTotal: formatMetric(input.secondaryTotal, metricTypeToUse, formatOptions),
    formattedLastPrimaryValue: formatMetric(input.lastPrimaryValue, metricTypeToUse, formatOptions),
    formattedLastSecondaryValue: formatMetric(input.lastSecondaryValue, metricTypeToUse, formatOptions),
    formattedPercentChange: formatMetric(Math.abs(input.percentChange), METRICTYPE.PERCENT, {
      decimalPlaces: 2
    }),
    formattedPercentChangeLastValues: formatMetric(Math.abs(input.percentChangeLastValues), METRICTYPE.PERCENT, {
      decimalPlaces: 2
    }),
    primaryAverageRate,
    secondaryAverageRate,
    formattedPrimaryAverageRate: formatMetric(primaryAverageRate, metricTypeToUse, formatOptions),
    formattedSecondaryAverageRate: formatMetric(secondaryAverageRate, metricTypeToUse, formatOptions)
  };
};
